import jsPDF from 'jspdf';
import logoText from '../assets/images/logo.png';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function printPatientPDF({
  patientData = {},
  operation = {},
  doctorName = 'Dr. No Name',
  signature = ''
}) {
  const { full_name, dob, location } = patientData;
  const {
    main_issue, therapy, ekg, examination, feedback, dg, xray, spo, ta,
    op_name, custom_service_name, init_date, doctor_signature, doctor_name,
  } = operation;

  const doctorSignature = doctor_signature || signature;
  const operationDoctorName = doctor_name || doctorName;

  const isPulmonology = patientData.category === 'pulmonology' ||
    (operationDoctorName && operationDoctorName.includes('Vera Lleshi'));

  const today = formatDate(new Date());

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  const pw = doc.internal.pageSize.getWidth();   // 210
  const ph = doc.internal.pageSize.getHeight();   // 297
  const ml = 14;                                   // left margin
  const mr = 14;                                   // right margin
  const cw = pw - ml - mr;                         // content width

  const teal    = '#0d9488';
  const tealBg  = '#f0fdfa';
  const dark    = '#1e293b';
  const muted   = '#64748b';
  const border  = '#cbd5e1';
  const white   = '#ffffff';

  // ── Background ──
  doc.setFillColor(white);
  doc.rect(0, 0, pw, ph, 'F');

  // ── Header ──
  doc.addImage(logoText, 'PNG', ml, 10, 12, 12);

  doc.setTextColor(teal);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Dental Med DIP SH.P.K.', pw / 2, 16, { align: 'center' });

  doc.setTextColor(muted);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(isPulmonology ? 'Raport Pulmologjik' : 'Raport Specialistik', pw / 2, 22, { align: 'center' });

  doc.setDrawColor(teal);
  doc.setLineWidth(0.6);
  doc.line(ml, 28, pw - mr, 28);

  // ── Patient info banner ──
  let y = 33;
  doc.setFillColor(tealBg);
  doc.setDrawColor(border);
  doc.setLineWidth(0.15);
  doc.roundedRect(ml, y, cw, 22, 1.5, 1.5, 'FD');

  const labelX = ml + 4;
  const valX = ml + 38;
  const col2LabelX = pw / 2 + 8;
  const col2ValX = pw / 2 + 38;

  const infoRow = (lbl, val, lx, vx, rowY) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(muted);
    doc.text(lbl, lx, rowY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(dark);
    doc.text(val || '-', vx, rowY);
  };

  const iy = y + 7;
  infoRow('Emri dhe mbiemri:', full_name || '-', labelX, valX, iy);
  infoRow('Viti i lindjes:', dob ? new Date(dob).getFullYear().toString() : '-', col2LabelX, col2ValX, iy);

  const services = (custom_service_name || op_name || '-').split(',').map(s => s.trim());
  infoRow('Shërbimi:', services[0], labelX, valX, iy + 7);
  infoRow('Data:', formatDate(init_date), col2LabelX, col2ValX, iy + 7);

  if (services.length > 1) {
    for (let i = 1; i < services.length; i++) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(dark);
      doc.text(services[i], valX, iy + 7 + i * 3.5);
    }
  }

  infoRow('Vendbanimi:', location || 'Gjakovë', labelX, valX, iy + 14);

  y += 26;

  // ── Medical details heading ──
  doc.setTextColor(teal);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Detajet Mjekësore', ml + 2, y);
  y += 2;
  doc.setDrawColor(teal);
  doc.setLineWidth(0.3);
  doc.line(ml, y, pw - mr, y);
  y += 5;

  // ── Helper: draw a labelled text block ──
  const maxTextWidth = cw - 36;
  const fieldLabelX = ml + 4;
  const fieldValX = ml + 38;
  const lineH = 3.2;
  const fieldGap = 4;

  const drawField = (label, text) => {
    if (!text || text.trim() === '' || text === 'Auto generated appointment') return;
    const lines = doc.splitTextToSize(text, maxTextWidth);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(teal);
    doc.text(label, fieldLabelX, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(dark);
    lines.forEach((line, i) => {
      doc.text(line, fieldValX, y + i * lineH);
    });
    y += Math.max(lineH, lines.length * lineH) + fieldGap;
  };

  // ── Helper: two small fields on one row ──
  const drawPairRow = (pairs) => {
    const validPairs = pairs.filter(p => p.val && p.val.trim() !== '');
    if (validPairs.length === 0) return;
    validPairs.forEach((p, idx) => {
      const lx = idx === 0 ? fieldLabelX : col2LabelX;
      const vx = idx === 0 ? fieldValX : col2ValX;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(teal);
      doc.text(p.lbl, lx, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(dark);
      doc.text(p.val, vx, y);
    });
    y += lineH + fieldGap;
  };

  // ── Render fields ──
  drawField('Ankesat Kryesore:', main_issue);
  drawField('Egzaminimi:', examination);

  if (isPulmonology) {
    drawField('Rtg PA pulmo:', xray);
    drawField('EKG:', ekg);
    drawPairRow([
      { lbl: 'TA:', val: ta },
      { lbl: 'SpO2:', val: spo }
    ]);
  } else {
    drawField('EKG:', ekg);
    drawField('Rtg:', xray);
    if (ta) drawField('TA:', ta);
    if (spo) drawField('SpO2:', spo);
  }

  drawField('DG:', dg);
  drawField('Terapia dhe mendimi:', therapy);
  drawField('Komente:', feedback);

  // ── Footer ──
  const footerTop = ph - 32;

  doc.setDrawColor(teal);
  doc.setLineWidth(0.4);
  doc.line(ml, footerTop, pw - mr, footerTop);

  doc.setTextColor(muted);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Gjakovë: ${today}`, ml + 3, footerTop + 6);

  if (isPulmonology) {
    doc.text('Numri kontaktues - 044287022', ml + 3, footerTop + 10);
    doc.text('KO-00568-01-04/6', ml + 3, footerTop + 14);
  }

  const displayName = isPulmonology
    ? 'Dr.Spec Interniste-Pulmologe Vera Lleshi'
    : operationDoctorName.replace('Dr,', 'Dr.');
  const nameWidth = doc.getTextWidth(displayName);
  const nameX = pw - mr - nameWidth;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(muted);
  doc.text('Mjeku:', nameX - 12, footerTop + 6);
  doc.setTextColor(dark);
  doc.text(displayName, nameX, footerTop + 6);

  if (doctorSignature) {
    try {
      const sigW = 32;
      const sigH = 10;
      const sigX = nameX + (nameWidth - sigW) / 2;
      doc.addImage(doctorSignature, 'PNG', sigX, footerTop + 9, sigW, sigH);
    } catch (err) {
      console.error('Error adding signature:', err);
    }
  }

  // ── Output ──
  doc.autoPrint();
  const printWindow = window.open(doc.output('bloburl'), '_blank');
  if (printWindow) {
    printWindow.document.title = `${full_name || 'Patient'} - Dental Med Record`;
  }
}
